// useFacturasEtiquetaXL - S48.2b.
// Hook de modo factura para el editor de etiquetas. Resuelve la factura
// (Venta) por id, mapea sus lineas a productos validos via una instancia
// independiente de useProductosListXL con per_page=1000, y aplica los
// criterios de skip:
//   - producto_id null               -> skip
//   - producto_id no encontrado      -> skip
//   - cantidad === 0                 -> skip
//   - es_excluido === true           -> skip
// Las lineas validas se devuelven como array `productosFactura` en el
// orden original de la factura (preserva indice). NO toca backend, NO
// instala libs.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useProductosListXL from '../../productos_xl/hooks/useProductosListXL';
import * as svc from '../../ventas/services/ventasService';

const PER_PAGE_FACTURA = 1000;

export default function useFacturasEtiquetaXL() {
  const inner = useProductosListXL();

  // En el primer mount, subir perPage para resolver la mayoria de los
  // productos referenciados por una factura sin paginacion.
  const bumped = useRef(false);
  useEffect(() => {
    if (bumped.current) return;
    bumped.current = true;
    if (typeof inner.setPerPage === 'function') {
      inner.setPerPage(PER_PAGE_FACTURA);
    }
  }, [inner]);

  const [factura, setFactura] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const productosFactura = useMemo(() => {
    if (!factura || !Array.isArray(factura.lineas)) return [];
    const items = Array.isArray(inner.items) ? inner.items : [];
    const byId = {};
    for (let i = 0; i < items.length; i += 1) {
      const p = items[i];
      if (p && p.id != null) byId[String(p.id)] = p;
    }
    const out = [];
    for (let i = 0; i < factura.lineas.length; i += 1) {
      const l = factura.lineas[i];
      if (!l) continue;
      if (l.producto_id == null) continue;
      const p = byId[String(l.producto_id)];
      if (!p) continue;
      const qty = Number(l.cantidad);
      if (!Number.isFinite(qty) || qty === 0) continue;
      if (l.es_excluido === true) continue;
      out.push(p);
    }
    return out;
  }, [factura, inner.items]);

  const cargarFactura = useCallback(async (id) => {
    if (id === null || id === undefined || id === '') return null;
    setLoading(true);
    setError(null);
    try {
      const v = await svc.getVenta(id);
      setFactura(v || null);
      return v || null;
    } catch (e) {
      setError((e && e.message) || 'Error al cargar la factura');
      setFactura(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const quitarFactura = useCallback(() => {
    setFactura(null);
    setError(null);
  }, []);

  const listarFacturas = useCallback(async (params) => {
    try {
      const r = await svc.listVentas(params || {});
      return r || { items: [], total: 0 };
    } catch (e) {
      return { items: [], total: 0 };
    }
  }, []);

  return {
    factura: factura,
    productosFactura: productosFactura,
    loading: loading,
    error: error,
    productosLoading: !!inner.loading,
    cargarFactura: cargarFactura,
    quitarFactura: quitarFactura,
    listarFacturas: listarFacturas,
  };
}
